// src/views/admin/AIReviewQueueModal.js
import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Text,
  VStack,
  HStack,
  Box,
  Badge,
  Textarea,
  Icon,
  useColorModeValue,
  useToast,
  Progress,
  Collapse,
  Divider,
  Flex
} from '@chakra-ui/react';
import { 
  MdThumbUp, 
  MdThumbDown, 
  MdCheckCircle, 
  MdPsychology,
  MdSkipNext
} from 'react-icons/md';
import aiService from '../../services/aiService';

const AIReviewQueueModal = ({ isOpen, onClose, queue = [], onQueueEmpty }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isCorrect, setIsCorrect] = useState(null); // true, false, or null (unselected)
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const toast = useToast();

  // Theme Colors
  const bgCard = useColorModeValue('gray.50', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const mutedText = useColorModeValue('gray.600', 'gray.400');

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(0);
      setIsCorrect(null);
      setNotes('');
    }
  }, [isOpen]);

  const currentItem = queue[currentIndex];
  const isFinished = currentIndex >= queue.length;

  const handleSubmit = async () => {
    if (!currentItem) return;

    setIsSubmitting(true);
    try {
      // Send the grade to the backend database to append to the Golden Dataset
      await aiService.submitPredictionGrade(
        currentItem.prediction_id, 
        currentItem.source_table, 
        isCorrect, 
        notes
      );

      toast({
        title: 'Feedback Saved',
        description: 'The AI will use this to improve future predictions.',
        status: 'success',
        duration: 2000,
        isClosable: true,
        position: 'top-right',
      });

      // Move to next item
      setCurrentIndex(prev => prev + 1);
      setIsCorrect(null);
      setNotes('');

    } catch (error) {
      toast({
        title: 'Error saving feedback',
        description: error.message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkip = () => {
    setCurrentIndex(prev => prev + 1);
    setIsCorrect(null);
    setNotes('');
  };

  const handleClose = () => {
    if (isFinished && onQueueEmpty) {
      onQueueEmpty(); // Trigger a refresh on the parent dashboard
    }
    onClose();
  };

  // --- RENDER: ALL CAUGHT UP STATE ---
  if (isFinished || queue.length === 0) {
    return (
      <Modal isOpen={isOpen} onClose={handleClose} isCentered>
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent>
          <ModalHeader textAlign="center">Review Complete</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={4} py={8}>
              <Icon as={MdCheckCircle} color="green.500" boxSize={16} />
              <Text fontSize="lg" fontWeight="bold">You're all caught up!</Text>
              <Text color={mutedText} textAlign="center">
                Thank you for helping train the FlowSense AI. Your feedback has been added to the Golden Dataset.
              </Text>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" w="full" onClick={handleClose}>
              Return to Dashboard
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  }

  // --- RENDER: ACTIVE REVIEW STATE ---
  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl" isCentered>
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent>
        <ModalHeader>
          <Flex justify="space-between" align="center">
            <HStack>
              <Icon as={MdPsychology} color="purple.500" />
              <Text>Human Verification Loop</Text>
            </HStack>
            <Badge colorScheme="purple" variant="subtle">
              {currentIndex + 1} of {queue.length}
            </Badge>
          </Flex>
          <Progress 
            value={((currentIndex) / queue.length) * 100} 
            size="xs" 
            colorScheme="purple" 
            mt={4} 
            borderRadius="full" 
          />
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <VStack spacing={6} align="stretch">
            
            {/* Context Box: What the AI looked at */}
            <Box p={4} bg={bgCard} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
              <Text fontSize="xs" fontWeight="bold" color={mutedText} textTransform="uppercase" mb={2}>
                Work Order Context
              </Text>
              <Text fontWeight="bold" mb={1}>{currentItem.title || `Work Order #${currentItem.work_order_id}`}</Text>
              <Text fontSize="sm" color={mutedText}>
                {currentItem.context || currentItem.description || "No description provided."}
              </Text>
            </Box>

            {/* Prediction Box: What the AI guessed */}
            <Box p={4} bg="purple.50" _dark={{ bg: 'purple.900' }} borderRadius="md" borderWidth="1px" borderColor="purple.200">
              <Text fontSize="xs" fontWeight="bold" color="purple.600" _dark={{ color: 'purple.300' }} textTransform="uppercase" mb={2}>
                AI Prediction ({currentItem.prediction_type || 'Assignment/Parts'})
              </Text>
              <Text fontSize="lg" fontWeight="bold">
                {currentItem.predicted_value || "Suggested Value"}
              </Text>
              {currentItem.confidence && (
                <Text fontSize="xs" mt={1} color="purple.600" _dark={{ color: 'purple.300' }}>
                  Confidence Score: {(currentItem.confidence * 100).toFixed(1)}%
                </Text>
              )}
            </Box>

            <Divider />

            {/* Grading Controls */}
            <VStack align="stretch" spacing={4}>
              <Text fontWeight="bold">Was this prediction correct?</Text>
              <HStack spacing={4}>
                <Button
                  flex="1"
                  size="lg"
                  variant={isCorrect === true ? 'solid' : 'outline'}
                  colorScheme={isCorrect === true ? 'green' : 'gray'}
                  leftIcon={<MdThumbUp />}
                  onClick={() => setIsCorrect(true)}
                >
                  Yes, Spot On
                </Button>
                <Button
                  flex="1"
                  size="lg"
                  variant={isCorrect === false ? 'solid' : 'outline'}
                  colorScheme={isCorrect === false ? 'red' : 'gray'}
                  leftIcon={<MdThumbDown />}
                  onClick={() => setIsCorrect(false)}
                >
                  No, Incorrect
                </Button>
              </HStack>

              {/* Conditional Notes Field if Incorrect */}
              <Collapse in={isCorrect === false} animateOpacity>
                <Box mt={2}>
                  <Text fontSize="sm" fontWeight="bold" mb={2}>
                    What should the AI have predicted?
                  </Text>
                  <Textarea
                    placeholder="e.g., 'This actually requires a 2-inch PVC valve, not 1-inch.' Provide the correct answer so the AI can relearn."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                  />
                </Box>
              </Collapse>
            </VStack>

          </VStack>
        </ModalBody>

        <ModalFooter bg={bgCard} borderBottomRadius="md" mt={4}>
          <Button variant="ghost" mr={3} onClick={handleSkip} rightIcon={<MdSkipNext />}>
            Skip for now
          </Button>
          <Button 
            colorScheme="purple" 
            onClick={handleSubmit} 
            isDisabled={isCorrect === null}
            isLoading={isSubmitting}
          >
            Submit Feedback
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AIReviewQueueModal;